import React, { useState, useEffect, useCallback } from 'react';
import PrincipalesSection from './PrincipalesSection';
import DerivadasSection from './DerivadasSection';
import LibresSection from './LibresSection';
import RequisitosSection from './RequisitosSection';
import usePuntosOrientacion from '../../hooks/usePuntosOrientacion';
import { useCPOContext } from '../../contexts/CPOContext';
import { STATUS } from '../../utils/careerLogic';

const getOrientStorageKeys = (orientacion) => {
  const idKey = orientacion?.id ? String(orientacion.id) : '';
  const nameKey = orientacion?.nombre ? String(orientacion.nombre) : '';
  const keys = [];
  if (idKey) keys.push(idKey);
  if (nameKey && nameKey !== idKey) keys.push(nameKey);
  return keys;
};

const OrientacionView = ({ orientacion, puntos, onPuntosChange, materias, updateMateriaEstado }) => {
  // console.log('OrientacionView rendering:', { orientacion, puntos });

  const { upsertMateriaCPO, removeMateriaCPO } = useCPOContext();

  const [materiasPrincipalesAprobadas, setMateriasPrincipalesAprobadas] = useState(() => ({}));

  const [materiasObligatorias, setMateriasObligatorias] = useState(() => ({
    constitucional: Boolean(puntos?.constitucionalProfundizado),
    metodos: Boolean(puntos?.procesalObligatorio && puntos.procesalObligatorio >= 2),
    concursos: Boolean(puntos?.procesalObligatorio && puntos.procesalObligatorio >= 4),
    integracion: Boolean(puntos?.integracion),
    lectocomprension: false, // Este no se guarda en puntos, siempre false por defecto
    practico: Boolean(puntos?.practico)
  }));

  const [materiasRegistradas, setMateriasRegistradas] = useState(() => {
    // Intentar cargar materias registradas desde localStorage (por id o nombre)
    const keys = getOrientStorageKeys(orientacion);
    const storageKey = keys.find(k => localStorage.getItem(`materiasRegistradas_${k}`));
    const materiasGuardadas = storageKey ? localStorage.getItem(`materiasRegistradas_${storageKey}`) : null;
    if (materiasGuardadas) {
      try {
        return JSON.parse(materiasGuardadas);
      } catch (error) {
        console.error('Error parsing materias registradas from localStorage:', error);
      }
    }
    
    return {
      derivadas: [],
      filosofia: [],
      sociales: [],
      procesalLibre: [],
      libresGeneralesCPO: []
    };
  });

  const [nuevaMateria, setNuevaMateria] = useState({
    nombre: '',
    puntos: '2',
    departamento: ''
  });

  const puntosCalculados = usePuntosOrientacion(
    orientacion,
    materiasPrincipalesAprobadas,
    materiasRegistradas,
    materiasObligatorias,
    onPuntosChange
  );

  // Reset the state when orientacion changes
  useEffect(() => {
    if (orientacion) {
      // Derivar materias principales desde el estado global (materias + contexto como respaldo)
      const estado = {};
      orientacion.materias.principales.forEach((m) => {
        const enMapa = materias?.[m.id];
        if (enMapa) {
          estado[m.id] = Boolean(enMapa.estado === STATUS.CURSADA_APROBADA || enMapa.notaFinal === -2);
        } else {
          estado[m.id] = false; // se actualizará si el contexto las tiene
        }
      });
      setMateriasPrincipalesAprobadas(estado);

      // Cargar materias registradas para esta orientación (por id o nombre)
      const keys = getOrientStorageKeys(orientacion);
      const regKey = keys.find(k => localStorage.getItem(`materiasRegistradas_${k}`));
      const materiasRegistradasGuardadas = regKey ? localStorage.getItem(`materiasRegistradas_${regKey}`) : null;
      if (materiasRegistradasGuardadas) {
        try {
          setMateriasRegistradas(JSON.parse(materiasRegistradasGuardadas));
        } catch (error) {
          console.error('Error loading materias registradas:', error);
          setMateriasRegistradas({
            derivadas: [],
            filosofia: [],
            sociales: [],
            procesalLibre: [],
            libresGeneralesCPO: []
          });
        }
      } else {
        setMateriasRegistradas({
          derivadas: [],
          filosofia: [],
          sociales: [],
          procesalLibre: [],
          libresGeneralesCPO: []
        });
      }

      // Cargar materias obligatorias para esta orientación (por id o nombre)
      const oblKey = keys.find(k => localStorage.getItem(`materiasObligatorias_${k}`));
      const materiasObligatoriasGuardadas = oblKey ? localStorage.getItem(`materiasObligatorias_${oblKey}`) : null;
      if (materiasObligatoriasGuardadas) {
        try {
          const obligatoriasGuardadas = JSON.parse(materiasObligatoriasGuardadas);
          setMateriasObligatorias(prev => ({
            ...prev,
            ...obligatoriasGuardadas
          }));
        } catch (error) {
          console.error('Error loading materias obligatorias:', error);
        }
      }
    }
  }, [orientacion, materias]);

  // Ya no persistimos estado local de principales: fuente de verdad es materias

  useEffect(() => {
    if (orientacion) {
      const keys = getOrientStorageKeys(orientacion);
      keys.forEach(k => localStorage.setItem(`materiasRegistradas_${k}`, JSON.stringify(materiasRegistradas)));
    }
  }, [materiasRegistradas, orientacion]);

  useEffect(() => {
    if (orientacion) {
      const keys = getOrientStorageKeys(orientacion);
      keys.forEach(k => localStorage.setItem(`materiasObligatorias_${k}`, JSON.stringify(materiasObligatorias)));
    }
  }, [materiasObligatorias, orientacion]);

  const handleMateriaChange = useCallback((materiaId, aprobada) => {
    setMateriasPrincipalesAprobadas(prev => ({
      ...prev,
      [materiaId]: aprobada
    }));
    // Única fuente: materias globales
    const globalMateria = materias?.[materiaId];
    if (globalMateria) {
      updateMateriaEstado(materiaId, aprobada ? STATUS.CURSADA_APROBADA : STATUS.NO_CURSADA, {
        notaFinal: aprobada ? -2 : -1,
      });
    } else {
      // Si no existe en plan (caso extremo), mantener en contexto para puntos
      const def = orientacion?.materias?.principales?.find(m => m.id === materiaId);
      if (def) {
        upsertMateriaCPO({
          id: def.id,
          nombre: def.nombre,
          creditos: Number(def.creditos || 0),
          categoria: `CPO Orientación ${orientacion.nombre} - Principal`,
          estado: aprobada ? 'CURSADA_APROBADA' : 'NO_CURSADA',
        });
      }
    }
  }, [materias, orientacion, updateMateriaEstado, upsertMateriaCPO]);

  const handleObligatoriaChange = useCallback((campo, valor) => {
    setMateriasObligatorias(prev => ({
      ...prev,
      [campo]: valor
    }));
    // Mapear toggles a materias del contexto CPO
    const mapToggleToMateria = (key) => {
      switch (key) {
        case 'constitucional':
          return { id: 'cpo_constitucional_profundizado', nombre: 'Constitucional Profundizado', creditos: 4, categoria: 'CPO Área Constitucional Profundizado' };
        case 'integracion':
          return { id: 'cpo_derecho_integracion', nombre: 'Derecho de la Integración', creditos: 4, categoria: 'CPO Libre Elección' };
        case 'metodos':
          return { id: 'cpo_procesal_metodos_alternativos', nombre: 'Métodos Alternativos de Resolución de Conflictos', creditos: 2, categoria: 'CPO Departamento Procesal' };
        case 'concursos':
          return { id: 'cpo_procesal_concursos', nombre: 'Concursos', creditos: 2, categoria: 'CPO Departamento Procesal' };
        case 'practico':
          return { id: 'cpo_practica_profesional', nombre: 'Práctica Profesional', creditos: 19, categoria: 'CPO Práctica Profesional' };
        default:
          return null;
      }
    };
    const materiaInfo = mapToggleToMateria(campo);
    if (materiaInfo) {
      upsertMateriaCPO({ ...materiaInfo, estado: valor ? 'CURSADA_APROBADA' : 'NO_CURSADA' });
    }
  }, [upsertMateriaCPO]);

  // Eliminado handlePuntosChange no utilizado

  const getMaxPuntos = (campo) => {
    const maxPuntos = {
      derivadas: orientacion.nombre === 'Derecho del Trabajo' ? 4 : 8,
      libresGeneralesCPO: 8,
      filosofia: 4,
      sociales: 4,
      procesalLibre: 2
    };
    return maxPuntos[campo] || 0;
  };

  const renderInfoBox = (title, content) => (
    <div style={{
      backgroundColor: 'var(--info-bg)',
      color: 'var(--info-text)',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid var(--info-border)'
    }}>
      <h4 style={{ margin: '0 0 10px 0', color: 'var(--info-text)' }}>{title}</h4>
      <div style={{ fontSize: '0.9em' }}>{content}</div>
    </div>
  );

  const renderCheckbox = (id, label, puntos, info = '') => (
    <div style={{
      backgroundColor: 'var(--bg-secondary)',
      color: 'var(--text-primary)',
      padding: '15px',
      borderRadius: '6px',
      marginBottom: '10px',
      border: materiasObligatorias[id] ? '2px solid var(--success-color)' : '1px solid var(--border-color)'
    }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        width: '100%'
      }}>
        <input
          type="checkbox"
          checked={materiasObligatorias[id] || false}
          onChange={(e) => handleObligatoriaChange(id, e.target.checked)}
          style={{
            width: '20px',
            height: '20px'
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '500' }}>{label}</div>
          <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>{puntos} puntos</div>
          {info && (
            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '5px' }}>
              {info}
            </div>
          )}
        </div>
        {materiasObligatorias[id] && (
          <span style={{
            backgroundColor: 'var(--status-cursada-bg)',
            color: 'var(--success-color)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.85em'
          }}>Aprobada</span>
        )}
      </label>
    </div>
  );

  const renderSeccion = (titulo, contenido, backgroundColor = 'var(--bg-secondary)') => (
    <div style={{
      backgroundColor,
      color: 'var(--text-primary)',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid var(--border-color)'
    }}>
      <h3 style={{ 
        margin: '0 0 15px 0', 
        color: 'var(--accent-color)',
        fontSize: '1.2em'
      }}>
        {titulo}
      </h3>
      {contenido}
    </div>
  );

  const agregarMateria = (seccion) => {
    if (!nuevaMateria.nombre.trim() || !nuevaMateria.puntos) {
      return;
    }

    const maxPuntos = getMaxPuntos(seccion);
    const puntosActuales = materiasRegistradas[seccion].reduce((sum, materia) => sum + Number(materia.puntos), 0);
    
    if (puntosActuales + Number(nuevaMateria.puntos) > maxPuntos) {
      alert(`No podés agregar más de ${maxPuntos} puntos en esta sección`);
      return;
    }

    const uniqueId = `${seccion}_${Date.now()}`;
    const nuevaMateriaObj = {
      id: uniqueId,
      nombre: nuevaMateria.nombre,
      puntos: Number(nuevaMateria.puntos),
      departamento: nuevaMateria.departamento
    };

    setMateriasRegistradas(prev => ({
      ...prev,
      [seccion]: [...prev[seccion], nuevaMateriaObj]
    }));

    // Reflejar en CPO global
    const categoriaPorSeccion = {
      derivadas: `CPO Orientación ${orientacion?.nombre || ''} - Derivada`,
      filosofia: 'CPO Departamento Filosofía',
      sociales: 'CPO Departamento Sociales',
      procesalLibre: 'CPO Departamento Procesal',
      libresGeneralesCPO: 'CPO Libre Elección',
    };
    const categoria = categoriaPorSeccion[seccion] || 'CPO Libre Elección';
    // Para procesalLibre, asegurar sufijo _libre en id para cómputo
    const idParaContexto = seccion === 'procesalLibre' ? `cpo_procesal_libre_${uniqueId}` : uniqueId;
    upsertMateriaCPO({
      id: idParaContexto,
      nombre: nuevaMateriaObj.nombre,
      creditos: nuevaMateriaObj.puntos,
      categoria,
      estado: 'CURSADA_APROBADA',
    });

    setNuevaMateria({ nombre: '', puntos: '2', departamento: '' });
  };

  const eliminarMateria = (seccion, id) => {
    setMateriasRegistradas(prev => ({
      ...prev,
      [seccion]: prev[seccion].filter(materia => materia.id !== id)
    }));
    // Eliminar del CPO global
    // Para procesalLibre, el id en contexto tiene prefijo 'cpo_procesal_libre_'
    const idContexto = seccion === 'procesalLibre' ? `cpo_procesal_libre_${id}` : id;
    removeMateriaCPO(idContexto);
  };

  const calcularPuntosTotales = (seccion) => {
    return materiasRegistradas[seccion].reduce((sum, materia) => sum + Number(materia.puntos), 0);
  };

  const renderFormularioMateria = (seccion, maxPuntos, departamentos = []) => {
    const puntosActuales = calcularPuntosTotales(seccion);
    const puntosRestantes = maxPuntos - puntosActuales;

    return (
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        padding: '15px',
        borderRadius: '6px',
        marginBottom: '15px',
        border: '1px solid var(--border-color)'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>
          Agregar Materia ({puntosRestantes} puntos disponibles)
        </h4>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Nombre de la materia"
            value={nuevaMateria.nombre}
            onChange={(e) => setNuevaMateria(prev => ({ ...prev, nombre: e.target.value }))}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          />
          <select
            value={nuevaMateria.puntos}
            onChange={(e) => setNuevaMateria(prev => ({ ...prev, puntos: e.target.value }))}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          >
            <option value="1">1 punto</option>
            <option value="2">2 puntos</option>
            <option value="3">3 puntos</option>
            <option value="4">4 puntos</option>
          </select>
          {departamentos.length > 0 && (
            <select
              value={nuevaMateria.departamento}
              onChange={(e) => setNuevaMateria(prev => ({ ...prev, departamento: e.target.value }))}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="">Departamento</option>
              {departamentos.map((depto, index) => (
                <option key={index} value={depto}>{depto}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => agregarMateria(seccion)}
            disabled={!nuevaMateria.nombre.trim() || puntosRestantes <= 0}
            style={{
              padding: '8px 16px',
              backgroundColor: puntosRestantes > 0 ? 'var(--accent-color)' : 'var(--disabled-bg)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: puntosRestantes > 0 ? 'pointer' : 'not-allowed',
              fontSize: '0.9em'
            }}
          >
            Agregar
          </button>
        </div>
      </div>
    );
  };

  const renderListaMaterias = (seccion, titulo) => {
    const materias = materiasRegistradas[seccion];
    const puntosTotales = calcularPuntosTotales(seccion);

    if (materias.length === 0) {
      return (
        <div style={{
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-secondary)',
          padding: '15px',
          borderRadius: '6px',
          textAlign: 'center',
          fontStyle: 'italic',
          border: '1px solid var(--border-color)'
        }}>
          No hay materias registradas
        </div>
      );
    }

    return (
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        padding: '15px',
        borderRadius: '6px',
        border: '1px solid var(--border-color)'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>
          {titulo} ({puntosTotales} puntos)
        </h4>
        {materias.map(materia => (
          <div key={materia.id} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '4px',
            marginBottom: '5px',
            border: '1px solid var(--border-color)'
          }}>
            <div>
              <div style={{ fontWeight: '500' }}>{materia.nombre}</div>
              {materia.departamento && (
                <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                  {materia.departamento}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                backgroundColor: 'var(--accent-color)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '0.8em'
              }}>
                {materia.puntos} pts
              </span>
              <button
                onClick={() => eliminarMateria(seccion, materia.id)}
                style={{
                  backgroundColor: 'var(--error-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  padding: '2px 6px',
                  cursor: 'pointer',
                  fontSize: '0.8em'
                }}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!orientacion) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: 'var(--text-secondary)'
      }}>
        Seleccioná una orientación para ver los detalles
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        backgroundColor: 'var(--success-color)',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ margin: '0 0 5px 0' }}>{orientacion.nombre}</h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Total: {puntosCalculados.total || 0}/69 puntos CPO
        </p>
      </div>

      {renderInfoBox(
        'Información General',
        `Para graduarte necesitás completar 69 puntos CPO. Esta orientación te permite obtener hasta ${orientacion.puntosMaximos || 69} puntos.`
      )}

      {renderSeccion('Materias Principales', (
        <PrincipalesSection
          orientacion={orientacion}
          puntos={puntosCalculados}
          materiasPrincipalesAprobadas={materiasPrincipalesAprobadas}
          handleMateriaChange={handleMateriaChange}
          materias={materias}
        />
      ))}

      {renderSeccion('Materias Derivadas', (
        <DerivadasSection
          orientacion={orientacion}
          puntos={puntosCalculados}
          renderFormularioMateria={renderFormularioMateria}
          renderListaMaterias={renderListaMaterias}
        />
      ))}

      {renderSeccion('Materias Libres', (
        <LibresSection
          orientacion={orientacion}
          puntos={puntosCalculados}
          renderFormularioMateria={renderFormularioMateria}
          renderListaMaterias={renderListaMaterias}
        />
      ))}

      <RequisitosSection
        renderSeccion={renderSeccion}
        renderCheckbox={renderCheckbox}
        renderFormularioMateria={renderFormularioMateria}
        renderListaMaterias={renderListaMaterias}
      />
    </div>
  );
};

export default OrientacionView;
